import React from "react";
import { CCol, CFormGroup, CInput, CLabel, CRow } from "@coreui/react";
import { useRemissionNoteFormContext } from "../context/RemissionNoteFormContext";
import DepartmentSelect from "../../location/DepartmentSelect";
import DistrictSelect from "../../location/DistrictSelect";
import CitySelect from "../../location/CitySelect";
import { FieldErrors } from "../../form/FieldErrors";
import { getFieldErrors } from "../../../models/errors";

const SectionDivider: React.FC<{ icon: string; label: string }> = ({ icon, label }) => (
  <div style={{ display: "flex", alignItems: "center", marginBottom: "16px", marginTop: "16px" }}>
    <div style={{ width: "4px", height: "18px", background: "#006657", borderRadius: "2px", marginRight: "10px", flexShrink: 0 }} />
    <i className={`fa ${icon} mr-2`} style={{ fontSize: "0.85rem", color: "var(--theme-accent)" }}></i>
    <h6 style={{ margin: 0, fontWeight: 600, color: "var(--theme-muted)", fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.8px" }}>
      {label}
    </h6>
    <div style={{ flex: 1, height: "1px", background: "var(--theme-divider)", marginLeft: "10px" }} />
  </div>
);

const FormLabel: React.FC<{ required?: boolean; children: React.ReactNode }> = ({ required, children }) => (
  <CLabel className="mb-0" style={{ fontWeight: 600, fontSize: "0.875rem", color: "var(--theme-text-soft)" }}>
    {required && <span className="text-danger mr-1">*</span>}
    {children}
  </CLabel>
);

export const RemissionNoteFormLocationsSection: React.FC = () => {
  const {
    remissionNoteRef,
    transportDepartureDepartment,
    transportDepartureDistrict,
    transportDepartureCity,
    transportArrivalDepartment,
    transportArrivalDistrict,
    transportArrivalCity,
    errors,
    onTransportDepartureAddressChange,
    onTransportDepartureHouseNumberChange,
    onTransportDepartureDepartmentChange,
    onTransportDepartureDistrictChange,
    onTransportDepartureCityChange,
    onTransportArrivalAddressChange,
    onTransportArrivalHouseNumberChange,
    onTransportArrivalDepartmentChange,
    onTransportArrivalDistrictChange,
    onTransportArrivalCityChange,
  } = useRemissionNoteFormContext();

  return (
    <>
      <SectionDivider icon="fa-arrow-right" label="Dirección de Salida" />
      <CFormGroup>
        <CRow className="align-items-center">
          <CCol md={2}>
            <FormLabel required>Departamento:</FormLabel>
          </CCol>
          <CCol md={2}>
            <DepartmentSelect
              onChange={onTransportDepartureDepartmentChange}
              value={transportDepartureDepartment}
              placeholder="Departamento"
            />
            <FieldErrors
              errors={
                getFieldErrors("transportDepartureDepartment", errors) as string[]
              }
            ></FieldErrors>
          </CCol>
          <CCol md={2}>
            <FormLabel required>Distrito:</FormLabel>
          </CCol>
          <CCol md={2}>
            <DistrictSelect
              onChange={onTransportDepartureDistrictChange}
              value={transportDepartureDistrict}
              placeholder="Distrito"
              departmentValue={
                transportDepartureDepartment !== null
                  ? transportDepartureDepartment
                  : undefined
              }
            />
            <FieldErrors
              errors={
                getFieldErrors("transportDepartureDistrict", errors) as string[]
              }
            ></FieldErrors>
          </CCol>
          <CCol md={2}>
            <FormLabel required>Ciudad:</FormLabel>
          </CCol>
          <CCol>
            <CitySelect
              onChange={onTransportDepartureCityChange}
              placeholder="Ciudad"
              value={transportDepartureCity}
              district={
                transportDepartureDistrict !== null
                  ? transportDepartureDistrict
                  : undefined
              }
              department={
                transportDepartureDepartment !== null
                  ? transportDepartureDepartment
                  : undefined
              }
            />
            <FieldErrors
              errors={
                getFieldErrors("transportDepartureCity", errors) as string[]
              }
            ></FieldErrors>
          </CCol>
        </CRow>
      </CFormGroup>
      <CFormGroup>
        <CRow className="align-items-center">
          <CCol md={2}>
            <FormLabel required>Dirección:</FormLabel>
          </CCol>
          <CCol md={6}>
            <CInput
              type="text"
              defaultValue={
                remissionNoteRef.current
                  ? remissionNoteRef.current.transportDepartureAddress
                  : ""
              }
              onChange={onTransportDepartureAddressChange}
              placeholder="Dirección"
            ></CInput>
            <FieldErrors
              errors={
                getFieldErrors("transportDepartureAddress", errors) as string[]
              }
            ></FieldErrors>
          </CCol>
          <CCol md={2}>
            <FormLabel required>Nro. de casa:</FormLabel>
          </CCol>
          <CCol md={2}>
            <CInput
              type="number"
              defaultValue={
                remissionNoteRef.current
                  ? remissionNoteRef.current.transportDepartureHouseNumber
                  : undefined
              }
              onChange={onTransportDepartureHouseNumberChange}
              placeholder="Número de casa"
            ></CInput>
            <FieldErrors
              errors={
                getFieldErrors("transportDepartureHouseNumber", errors) as string[]
              }
            ></FieldErrors>
          </CCol>
        </CRow>
      </CFormGroup>

      <SectionDivider icon="fa-flag-checkered" label="Dirección de Entrega" />
      <CFormGroup>
        <CRow className="align-items-center">
          <CCol md={2}>
            <FormLabel required>Departamento:</FormLabel>
          </CCol>
          <CCol md={2}>
            <DepartmentSelect
              onChange={onTransportArrivalDepartmentChange}
              value={transportArrivalDepartment}
              placeholder="Departamento"
            />
            <FieldErrors
              errors={
                getFieldErrors("transportArrivalDepartment", errors) as string[]
              }
            ></FieldErrors>
          </CCol>
          <CCol md={2}>
            <FormLabel required>Distrito:</FormLabel>
          </CCol>
          <CCol md={2}>
            <DistrictSelect
              onChange={onTransportArrivalDistrictChange}
              value={transportArrivalDistrict}
              placeholder="Distrito"
              departmentValue={
                transportArrivalDepartment !== null
                  ? transportArrivalDepartment
                  : undefined
              }
            />
            <FieldErrors
              errors={
                getFieldErrors("transportArrivalDistrict", errors) as string[]
              }
            ></FieldErrors>
          </CCol>
          <CCol md={2}>
            <FormLabel required>Ciudad:</FormLabel>
          </CCol>
          <CCol>
            <CitySelect
              onChange={onTransportArrivalCityChange}
              placeholder="Ciudad"
              value={transportArrivalCity}
              district={
                transportArrivalDistrict !== null
                  ? transportArrivalDistrict
                  : undefined
              }
              department={
                transportArrivalDepartment !== null
                  ? transportArrivalDepartment
                  : undefined
              }
            />
            <FieldErrors
              errors={
                getFieldErrors("transportArrivalCity", errors) as string[]
              }
            ></FieldErrors>
          </CCol>
        </CRow>
      </CFormGroup>
      <CFormGroup>
        <CRow className="align-items-center">
          <CCol md={2}>
            <FormLabel required>Dirección:</FormLabel>
          </CCol>
          <CCol md={6}>
            <CInput
              type="text"
              defaultValue={
                remissionNoteRef.current
                  ? remissionNoteRef.current.transportArrivalAddress
                  : ""
              }
              onChange={onTransportArrivalAddressChange}
              placeholder="Dirección"
            ></CInput>
            <FieldErrors
              errors={
                getFieldErrors("transportArrivalAddress", errors) as string[]
              }
            ></FieldErrors>
          </CCol>
          <CCol md={2}>
            <FormLabel required>Nro. de casa:</FormLabel>
          </CCol>
          <CCol md={2}>
            <CInput
              type="number"
              defaultValue={
                remissionNoteRef.current
                  ? remissionNoteRef.current.transportArrivalHouseNumber
                  : undefined
              }
              onChange={onTransportArrivalHouseNumberChange}
              placeholder="Número de casa"
            ></CInput>
            <FieldErrors
              errors={
                getFieldErrors("transportArrivalHouseNumber", errors) as string[]
              }
            ></FieldErrors>
          </CCol>
        </CRow>
      </CFormGroup>
    </>
  );
};
